/**
 * The simulator page: the SimulatorWorkbench, full page and with nothing around it. Empty, a
 * conversation is authored from scratch; `?scenario=` loads that scenario and `?run=` reopens
 * a kept run as it ended. The address is the whole of what the page shows, so a copied link
 * opens the same thing in another tab.
 */

import { useCallback, useEffect, useEffectEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BACK_TESTING_PATH, SIMULATOR_PATH } from '@/constants/paths';
import { toLocalScenario, toScenarioCase } from '@/modules/back-testing/types';
import { restoreRunAtom } from '@/modules/simulator/session/restore-run';
import {
	endSimulatorSessionAtom,
	openSimulatorSessionAtom,
	runOnScreenAtom,
	simulatorSessionAtom
} from '@/modules/simulator/session/simulator-session.store';
import { SimulatorWorkbench } from '@/modules/simulator/simulator-workbench';
import { aiFunctionService } from '@/services/ai-function.service';
import { simulatorRunService } from '@/services/simulator-run.service';

type Load = 'loading' | 'ready' | 'missing' | 'unavailable';

/** Only a 404 means the thing is gone; anything else means we could not ask. */
const isNotFound = (error: unknown): boolean =>
	typeof error === 'object' && error !== null && (error as { response?: { status?: number } }).response?.status === 404;

const Loading = () => (
	<div className='flex h-svh items-center justify-center'>
		<Loader2 className='size-5 animate-spin text-muted-foreground' />
	</div>
);

const NotLoaded = ({ message, to, label }: { message: string; to: string; label: string }) => (
	<div className='flex h-svh flex-col items-center justify-center gap-3 px-6 text-center'>
		<p className='max-w-sm text-sm text-muted-foreground'>{message}</p>
		<Button variant='outline' size='sm' asChild>
			<Link to={to}>{label}</Link>
		</Button>
	</div>
);

/** The scenario named in the URL, loaded alone into the simulator. */
const LoadedScenario = ({ scenarioId, onCleared }: { scenarioId: string; onCleared: () => void }) => {
	const openInSimulator = useSetAtom(openSimulatorSessionAtom);
	const session = useAtomValue(simulatorSessionAtom);
	// Coming back to a scenario that is still on screen keeps its run; only a scenario
	// that is not on screen is fetched and loaded.
	const [returning] = useState(
		() => session !== null && session.activeIndex !== null && session.cases[session.activeIndex]?.id === scenarioId
	);
	const [state, setState] = useState<Load>(returning ? 'ready' : 'loading');

	useEffect(() => {
		if (returning) return;
		let live = true;
		setState('loading');
		aiFunctionService
			.fetchScenario(scenarioId)
			.then((row) => {
				if (!live) return;
				openInSimulator({
					cases: [toScenarioCase(toLocalScenario(row.sopId, row))],
					activeIndex: 0,
					origin: { sopId: row.sopId }
				});
				setState('ready');
			})
			.catch((error: unknown) => {
				if (live) setState(isNotFound(error) ? 'missing' : 'unavailable');
			});
		return () => {
			live = false;
		};
	}, [scenarioId, openInSimulator, returning]);

	// Reset unloads the case, so the address stops naming it; otherwise a reload would bring
	// back what was just cleared.
	useEffect(() => {
		if (state === 'ready' && session && session.activeIndex === null) onCleared();
	}, [state, session, onCleared]);

	if (state === 'loading') return <Loading />;
	if (state !== 'ready') {
		return (
			<NotLoaded
				message={
					state === 'missing'
						? 'This scenario is no longer there. It may have been deleted, or its type regenerated.'
						: 'This scenario could not be loaded. The link is fine, try again in a moment.'
				}
				to={BACK_TESTING_PATH}
				label='Back to back testing'
			/>
		);
	}

	return (
		<div className='h-svh'>
			<SimulatorWorkbench variant='runner' />
		</div>
	);
};

/**
 * The kept run named in the URL, reopened when it is not the one on screen. It covers the
 * simulator while it loads instead of replacing it, so naming the run already on screen
 * never remounts the simulator and loses what is being typed.
 */
const RunAddress = ({ runId, onCleared }: { runId: string; onCleared: () => void }) => {
	const reopenRun = useSetAtom(restoreRunAtom);
	const runOnScreen = useAtomValue(runOnScreenAtom);
	const [returning] = useState(() => runOnScreen === runId);
	const [state, setState] = useState<Load>(returning ? 'ready' : 'loading');

	useEffect(() => {
		if (returning) return;
		let live = true;
		setState('loading');
		simulatorRunService
			.fetchRun(runId)
			.then((run) => {
				if (!live) return;
				reopenRun(run);
				setState('ready');
			})
			.catch((error: unknown) => {
				if (live) setState(isNotFound(error) ? 'missing' : 'unavailable');
			});
		return () => {
			live = false;
		};
	}, [runId, returning, reopenRun]);

	// Reset, or a thread loaded over it, takes the run off screen, so the address stops naming it.
	useEffect(() => {
		if (state === 'ready' && runOnScreen === null) onCleared();
	}, [state, runOnScreen, onCleared]);

	if (state === 'ready') return null;
	return (
		<div className='absolute inset-0 z-10 bg-background'>
			{state === 'loading' ? (
				<Loading />
			) : (
				<NotLoaded
					message={
						state === 'missing'
							? 'This run is not kept on this account. It may have been deleted, or belong to another account.'
							: 'This run could not be loaded. The link is fine, try again in a moment.'
					}
					to={SIMULATOR_PATH}
					label='Open the simulator'
				/>
			)}
		</div>
	);
};

export default function Playground() {
	const [searchParams, setSearchParams] = useSearchParams();
	const scenarioId = searchParams.get('scenario') ?? '';
	const runId = searchParams.get('run') ?? '';
	const endSimulatorSession = useSetAtom(endSimulatorSessionAtom);
	const runOnScreen = useAtomValue(runOnScreenAtom);

	// Reset means reset: the case goes, the hand-off that carried it goes, and the
	// address stops naming it, leaving the page it would have been opened on.
	const handleCleared = useCallback(() => {
		endSimulatorSession();
		setSearchParams({}, { replace: true });
	}, [endSimulatorSession, setSearchParams]);

	const handleRunCleared = useCallback(() => setSearchParams({}, { replace: true }), [setSearchParams]);

	// The address names the kept run on screen, so a reload or a copied link reopens it. It
	// follows the screen, not the address: going back to another run's link loads that run.
	// A scenario keeps its own address.
	const nameRunOnScreen = useEffectEvent((requestId: string) => {
		if (!scenarioId && requestId !== runId) setSearchParams({ run: requestId }, { replace: true });
	});
	useEffect(() => {
		if (runOnScreen) nameRunOnScreen(runOnScreen);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- effect events are stable; this plugin version predates useEffectEvent
	}, [runOnScreen]);

	// Keyed, so pasting a different scenario or run into the address bar loads that one
	// rather than leaving the previous one's thread in place.
	if (scenarioId) {
		return <LoadedScenario key={scenarioId} scenarioId={scenarioId} onCleared={handleCleared} />;
	}

	return (
		<div className='relative h-svh'>
			<SimulatorWorkbench />
			{runId && <RunAddress key={runId} runId={runId} onCleared={handleRunCleared} />}
		</div>
	);
}
